import { RAMP_CURB, garageRamp, quatToYaw, rampFootprint } from "@uzverse/world-scene";
import type { GarageRamp, SceneNode } from "@uzverse/world-scene";
import type { Collider } from "../camera/walk.js";
import { parseStyle } from "../brand.js";
import { doorOf } from "../generators.js";

/**
 * The underground garage of a building with `style.garage`: a ramp cut into the ground beside the
 * building, reaching a rolling gate under the wall. Pure geometry of the ramp footprint, shared by
 * the renderer (the ground gets a hole there), the walker (the pit is fenced), and the blueprint
 * rules (the ramp must fit where the building is dropped).
 */

export { RAMP_CURB, RAMP_DEPTH, RAMP_LENGTH, RAMP_WIDTH } from "@uzverse/world-scene";
export type GarageSpec = GarageRamp;

/** The ramp of a building's params, or null when it has no garage. */
export function garageOf(params: Record<string, unknown>): GarageSpec | null {
  if (!parseStyle(params["style"]).garage) return null;
  return garageRamp({
    width: num(params["width"], 20),
    depth: num(params["depth"], 20),
    doorSide: doorOf(params).side,
  });
}

const num = (v: unknown, d: number) =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? v : d;

/** The ramp in world space as an oriented rectangle (a collider-shaped box, `hw` along the ramp). */
export function garageWorld(
  node: SceneNode,
): (Collider & { length: number; width: number }) | null {
  if (node.geometry.kind !== "procedural" || node.geometry.generator !== "building.box")
    return null;
  const g = garageOf(node.geometry.params);
  if (!g) return null;
  const yaw = quatToYaw(node.transform.rotation) ?? 0;
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  return {
    x: node.transform.position[0] + g.cx * c + g.cz * s,
    z: node.transform.position[2] - g.cx * s + g.cz * c,
    yaw: yaw + g.yaw,
    hw: g.length / 2,
    hd: g.width / 2,
    top: 0,
    length: g.length,
    width: g.width,
  };
}

/** Fences around the opening for the walker (the pit is not walkable; the curb is 1 m tall to it). */
export function garageColliders(nodes: Iterable<SceneNode>): Collider[] {
  const out: Collider[] = [];
  for (const n of nodes) {
    const g = garageWorld(n);
    if (!g) continue;
    out.push({
      x: g.x,
      z: g.z,
      yaw: g.yaw,
      hw: g.hw + RAMP_CURB,
      hd: g.hd + RAMP_CURB,
      top: 1,
    });
  }
  return out;
}

/** The ramp as a placement footprint for a candidate pose of the building (the blueprint rules). */
export function rampCandidate(
  params: Record<string, unknown>,
  pose: { x: number; z: number; yawRad: number },
): { x: number; z: number; yawRad: number; width: number; depth: number } | null {
  if (!garageOf(params)) return null;
  return rampFootprint(
    {
      width: num(params["width"], 20),
      depth: num(params["depth"], 20),
      doorSide: doorOf(params).side,
    },
    pose,
  );
}

/** The ramps to cut into the ground (world space), at most as many as the shader can hold. */
export function garageHoles(nodes: Iterable<SceneNode>): Collider[] {
  const out: Collider[] = [];
  for (const n of nodes) {
    const g = garageWorld(n);
    if (g) out.push(g);
  }
  return out.slice(0, 4);
}
